import i18n from '@/i18n'
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  type NotificationEventType,
  type NotificationSettingsState,
} from '@/types/notifications';

type SettingsRow = {
  account_id: string;
  whatsapp_enabled: boolean;
  lead_assigned: boolean;
  lead_campaign_entered: boolean;
  followup_handoff: boolean;
  calendar_ai_created: boolean;
  opportunity_pipeline_changed: boolean;
  opportunity_won: boolean;
  opportunity_lost: boolean;
  conversation_opened: boolean;
  conversation_closed: boolean;
  whatsapp_connected: boolean;
};

function rowToState(row: SettingsRow): NotificationSettingsState {
  return {
    whatsappEnabled: row.whatsapp_enabled === true,
    events: {
      'lead.assigned': row.lead_assigned === true,
      'lead.campaign_entered': row.lead_campaign_entered === true,
      'followup.handoff': row.followup_handoff === true,
      'calendar.ai_created': row.calendar_ai_created === true,
      'opportunity.pipeline_changed': row.opportunity_pipeline_changed === true,
      'opportunity.won': row.opportunity_won === true,
      'opportunity.lost': row.opportunity_lost === true,
      'conversation.opened': row.conversation_opened === true,
      'conversation.closed': row.conversation_closed === true,
      'whatsapp.connected': row.whatsapp_connected === true,
    },
  };
}

function stateToRow(accountId: string, state: NotificationSettingsState) {
  return {
    account_id: accountId,
    whatsapp_enabled: state.whatsappEnabled,
    lead_assigned: state.events['lead.assigned'],
    lead_campaign_entered: state.events['lead.campaign_entered'],
    followup_handoff: state.events['followup.handoff'],
    calendar_ai_created: state.events['calendar.ai_created'],
    opportunity_pipeline_changed: state.events['opportunity.pipeline_changed'],
    opportunity_won: state.events['opportunity.won'],
    opportunity_lost: state.events['opportunity.lost'],
    conversation_opened: state.events['conversation.opened'],
    conversation_closed: state.events['conversation.closed'],
    // Opção removida da UI — mantém desligado no banco.
    whatsapp_connected: false,
  };
}

function settingsEqual(a: NotificationSettingsState, b: NotificationSettingsState) {
  if (a.whatsappEnabled !== b.whatsappEnabled) return false;
  const keys = Object.keys(a.events) as NotificationEventType[];
  return keys.every((key) => a.events[key] === b.events[key]);
}

export function useNotificationSettings() {
  const { user } = useAuth();
  const accountId = user?.account_id ?? null;

  const [settings, setSettings] = useState<NotificationSettingsState>(DEFAULT_NOTIFICATION_SETTINGS);
  const [savedSettings, setSavedSettings] = useState<NotificationSettingsState>(
    DEFAULT_NOTIFICATION_SETTINGS
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isDirty = useMemo(
    () => !settingsEqual(settings, savedSettings),
    [settings, savedSettings]
  );

  const load = useCallback(async () => {
    if (!accountId || !supabase) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      await supabase
        .from('account_notification_settings')
        .upsert({ account_id: accountId }, { onConflict: 'account_id', ignoreDuplicates: true });

      const { data, error } = await supabase
        .from('account_notification_settings')
        .select('*')
        .eq('account_id', accountId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        const next = rowToState(data as SettingsRow);
        setSettings(next);
        setSavedSettings(next);
      }
    } catch (error) {
      console.error('[useNotificationSettings] load error:', error);
      toast.error(i18n.t('settings:notifications.loadError'));
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const setWhatsappEnabled = (checked: boolean) => {
    setSettings((current) => ({ ...current, whatsappEnabled: checked }));
  };

  const setEventEnabled = (key: NotificationEventType, checked: boolean) => {
    setSettings((current) => ({
      ...current,
      events: { ...current.events, [key]: checked },
    }));
  };

  const save = async () => {
    if (!accountId || !supabase) return false;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('account_notification_settings')
        .upsert(stateToRow(accountId, settings), { onConflict: 'account_id' });

      if (error) throw error;

      setSavedSettings(settings);
      toast.success(i18n.t('settings:notifications.saved'), {
        description: 'As configurações de notificação foram atualizadas.',
      });
      return true;
    } catch (error) {
      console.error('[useNotificationSettings] save error:', error);
      toast.error(i18n.t('settings:notifications.saveError'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    settings,
    loading,
    saving,
    isDirty,
    setWhatsappEnabled,
    setEventEnabled,
    save,
    reload: load,
  };
}
